import { HTMLAttributes } from "react";

import { useI18n } from "../use-i18n";
import { cn } from "../utils";
import { NcButton } from "./nc-button";
import { NcLoadingIndicator } from "./nc-loading-indicator";

const liBaseClasses = "group not-first-of-type:-ml-[2px] hover:z-10 focus-within:z-10 hidden";
const buttonBaseClasses = "relative";
const selectedPageButtonClasses = [
  "group-aria-[current=page]:border-active group-aria-[current=page]:bg-active",
  "group-aria-[current=page]:text-light group-aria-[current=page]:cursor-default",
  "group-aria-[current=page]:data-hovered:z-10 group-aria-[current=page]:data-focus-visible:z-10",
];

interface NcPaginatorProps extends HTMLAttributes<HTMLElement> {
  currentPage: number;
  totalPages: number;
  zeroBasedPagination?: boolean;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

const NcPaginator = ({
  currentPage,
  totalPages,
  zeroBasedPagination,
  onPageChange,
  isLoading,
  isDisabled,
  className,
  ...props
}: NcPaginatorProps) => {
  const { t } = useI18n();
  const firstPage = zeroBasedPagination ? 0 : 1;
  const lastPage = zeroBasedPagination ? totalPages - 1 : totalPages;

  const maxCurrentAdjacentPages = 2;
  const windowSize = maxCurrentAdjacentPages * 2 + 1;
  const windowStart = Math.max(
    firstPage,
    Math.min(currentPage - maxCurrentAdjacentPages, lastPage - windowSize + 1)
  );
  const windowEnd = Math.min(lastPage, windowStart + windowSize - 1);

  const pages: number[] = [];
  for (let p = windowStart; p <= windowEnd; p++) {
    pages.push(p);
  }

  const showLeftEllipsis = windowStart > firstPage;
  const showRightEllipsis = windowEnd < lastPage;
  const leftEllipsisPage = windowStart - 1;
  const rightEllipsisPage = windowEnd + 1;

  function getPageAriaLabel(page: number, pageLabel: number) {
    const isCurrentPage = page === currentPage;
    return isCurrentPage ? `${t("current page")}, ${pageLabel}` : `${t("go to page")} ${pageLabel}`;
  }

  function getEllipsisAriaLabel(page: number) {
    const pageLabel = zeroBasedPagination ? page + 1 : page;
    return `${t("go to page")} ${pageLabel}`;
  }

  return (
    <nav
      data-nc="NcPaginator"
      role="navigation"
      aria-label={t("Pagination navigation")}
      className={cn("basis-full", className)}
      {...props}
    >
      <ul className="@container isolate flex">
        {isLoading ? (
          <li className="py-2">
            <NcLoadingIndicator />
          </li>
        ) : (
          <>
            <li className={cn([liBaseClasses, "@2xl:flex"])}>
              <NcButton
                className={cn(buttonBaseClasses, "rounded-r-none")}
                isDisabled={currentPage <= firstPage || isDisabled}
                onPress={() => onPageChange(firstPage)}
              >
                {t("First")}
              </NcButton>
            </li>

            <li className={cn([liBaseClasses, "flex"])}>
              <NcButton
                className={cn(buttonBaseClasses, "rounded-none @max-2xl:rounded-l-md")}
                isDisabled={currentPage <= firstPage || isDisabled}
                onPress={() => onPageChange(currentPage - 1)}
              >
                {t("Previous")}
              </NcButton>
            </li>

            {showLeftEllipsis && (
              <li className={cn([liBaseClasses, "@md:flex"])}>
                <NcButton
                  className={cn(buttonBaseClasses, "rounded-none")}
                  onPress={() => onPageChange(leftEllipsisPage)}
                  aria-label={getEllipsisAriaLabel(leftEllipsisPage)}
                  isDisabled={isDisabled}
                >
                  …
                </NcButton>
              </li>
            )}

            {pages.map(page => {
              const pageLabel = zeroBasedPagination ? page + 1 : page;
              const pageAriaLabel = getPageAriaLabel(page, pageLabel);
              return (
                <li
                  key={page}
                  className={cn([
                    liBaseClasses,
                    "@lg:flex",
                    "aria-[current=page]:z-10 aria-[current=page]:flex",
                  ])}
                  aria-current={page === currentPage ? "page" : undefined}
                >
                  <NcButton
                    className={cn(buttonBaseClasses, selectedPageButtonClasses, "rounded-none")}
                    onPress={() => onPageChange(page)}
                    aria-label={pageAriaLabel}
                    isDisabled={isDisabled}
                  >
                    {pageLabel}
                  </NcButton>
                </li>
              );
            })}

            {showRightEllipsis && (
              <li className={cn([liBaseClasses, "@md:flex"])}>
                <NcButton
                  className={cn(buttonBaseClasses, "rounded-none")}
                  onPress={() => onPageChange(rightEllipsisPage)}
                  aria-label={getEllipsisAriaLabel(rightEllipsisPage)}
                  isDisabled={isDisabled}
                >
                  …
                </NcButton>
              </li>
            )}

            <li className={cn([liBaseClasses, "flex"])}>
              <NcButton
                className={cn(buttonBaseClasses, "rounded-none @max-2xl:rounded-r-md")}
                isDisabled={currentPage >= lastPage || isDisabled}
                onPress={() => onPageChange(currentPage + 1)}
              >
                {t("Next")}
              </NcButton>
            </li>

            <li className={cn([liBaseClasses, "@2xl:flex"])}>
              <NcButton
                className={cn(buttonBaseClasses, "rounded-l-none")}
                isDisabled={currentPage >= lastPage || isDisabled}
                onPress={() => onPageChange(lastPage)}
              >
                {t("Last")}
              </NcButton>
            </li>
          </>
        )}
      </ul>
    </nav>
  );
};

export { NcPaginator };
